// The two URL namespaces the cross-device preview owns, and the pure matcher that decides which is
// which. No imports — src/index.tsx consults this during boot, before anything else loads.
//
// WHY TWO NAMESPACES. The receiver page is an SPA route (the app must render it). JUTE's bundle files
// are service-worker territory (the app must NEVER render them). Sharing one prefix made those
// indistinguishable, and the failure was silent: a request the worker didn't answer fell through to the
// SPA fallback, which returns Designer's own index.html with HTTP 200 — so a service-worker problem
// showed up as "Designer is running inside my preview frame", with nothing in any log to say why.
// Separate prefixes make that state detectable, which is what turns it into a visible error.

/**
 * The versioned asset directory, with leading and trailing slashes — `/static/` locally, `/<pipelineId>/`
 * on a deployed environment.
 *
 * Everything the preview service workers touch hangs off this, because it is the only directory the deploy
 * pipeline uploads (`aws s3 sync ./dist/$VERSION`).
 */
export const PREVIEW_ASSET_ROOT = `/${__STATIC_PATH__}/`;

/** The page the QR points at. An SPA route; deliberately NOT covered by the worker's scope. */
export const RECEIVER_PATH = '/__remote-preview__/';

/**
 * JUTE's bundle files, one directory per session. The worker's scope; never an SPA route.
 *
 * UNDER THE VERSIONED ASSET DIRECTORY, not the site root. A service worker's scope is capped by where its
 * script is served from, and the deploy pipeline only uploads that one directory — so the worker has to
 * live inside it, and therefore so does everything it controls. See the note on `previewServiceWorkers` in
 * vite.config.ts for the deployment failure this fixes.
 *
 * JUTE is indifferent to the prefix: it resolves `content.json` and `fs/<id>` from
 * `window.location.pathname`, so an extra leading segment changes nothing for it. The worker likewise
 * derives every path from `registration.scope` rather than assuming a depth.
 */
export const JUTE_PATH_PREFIX = `${PREVIEW_ASSET_ROOT}__remote-jute__/`;

/**
 * The cross-device worker's script URL. Beside the namespace it controls, and deliberately derived from the
 * same root: a worker's scope is capped by its script's directory, so if these two ever drifted the
 * registration would fail with a scope error. One constant, no chance of disagreement.
 */
export const REMOTE_PREVIEW_SW_URL = `${PREVIEW_ASSET_ROOT}remote-preview-sw.js`;

export type RemotePreviewRoute =
    /** The receiver page itself — render RemotePreviewReceiver. */
    | 'receiver'
    /**
     * A path only the service worker should ever answer. If the app is being asked to render one, the
     * worker isn't controlling the scope and the SPA fallback served the shell instead — a hard error,
     * never Designer and never a second nested receiver.
     */
    | 'sw-miss'
    /** Everything else — the Designer app. */
    | 'app';

/**
 * Classify a pathname. Query strings are not consulted (`location.pathname` excludes them).
 *
 * Tolerates a duplicated leading slash: `?previewOrigin=https://host/` used to produce
 * `https://host//__remote-preview__/`, whose pathname is `//__remote-preview__/`. That missed a
 * `startsWith` check and fell through to the Designer boot, which — because `projectId` came out as the
 * truthy string `"__remote-preview__"` — sent the phone to an OAuth login page. Origins are normalised
 * at the source now (see settings.ts), but a matcher that only works on well-formed input is one
 * upstream slip away from that same login page, so it tolerates it here too.
 */
export function matchRemotePreviewRoute(pathname: string): RemotePreviewRoute {
    const path = pathname.replace(/^\/+/, '/');

    if (path === RECEIVER_PATH || path === RECEIVER_PATH.slice(0, -1)) return 'receiver';
    if (path.startsWith(JUTE_PATH_PREFIX)) return 'sw-miss';
    // Nothing legitimately lives deeper than the receiver page in its own namespace, so a deeper path
    // is a stale or mangled URL — report it rather than booting the editor on a phone.
    if (path.startsWith(RECEIVER_PATH)) return 'sw-miss';

    return 'app';
}

/** Directory-style URL for a session's JUTE bundle. The trailing slash is REQUIRED: JUTE resolves
 *  `content.json` and `fs/<id>` off `window.location.pathname` (PROJECT_PREVIEW.md pitfall #1), and the
 *  worker maps the trailing slash onto `index.html`. */
export function jutePathFor(remoteSessionId: string): string {
    return `${JUTE_PATH_PREFIX}${remoteSessionId}/`;
}
